// src/pages/CalculatorPage.js
//
// Auth-aware calculator flow.
//
// Both anonymous and authenticated visitors now land directly on step1 —
// the local "intro" screen this page used to have was removed once
// StartPage.js ('/') became the real front door (see
// portal_start_page_plan.md). Clicking "Start (Everyone Else)" on
// StartPage.js's Calculator card routes straight here, straight to step1.
//
// Ticket 014-partnerintro reintroduces a different, narrower intro screen
// on top of this: a one-time-per-anonymous-visit "{Partner} sent you to
// HolaSam…" step, shown only to partner-referred, unauthenticated visitors
// (see showPartnerIntro below). This does not contradict the removal
// described above — that was the old generic per-product intro every
// visitor saw; this one is partner-referral-specific and never shown to
// organic traffic.
//
// Anonymous mode:
//   step1 → step2 → step4 (result + email capture)
//
// Authenticated mode:
//   step1 → step2 → step4 (result auto-saved as a draft, owned by the
//   user; "Save & get link" publishes it explicitly)
//
// The calculate-irpf edge function accepts an optional Authorization header.
// When present, it resolves user_id + tax_subject_id and sets them on the
// row immediately (draft, not published). CalculatorPage passes the JWT
// when the user is authenticated; anonymous callers get the existing
// no-auth path.
//
// SESSION LIFECYCLE (draft/publish model):
//   Every runCalculation() call persists a draft — insert on the first
//   call, update in place on every subsequent one (useCalculatorForm.js
//   tracks the session_id and sends it back each time). Nothing is
//   publicly shareable or immutable until the user explicitly clicks
//   "Save & get link" in the sidebar, which calls publishSession() and
//   mints public_token. That's the only thing that ever makes a session
//   immutable — calculating never does.

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  useCalculatorForm,
  STEP_1_CONFIG,
  DEFAULT_FX_RATES,
  getEligibilityBlock,
} from '../hooks/useCalculatorForm'
import { useProductMode } from '../hooks/useProductMode'
import { supabase } from '../lib/supabase'
import { fetchPartnerNameByRef } from '../lib/partnerLookup'
import { getActivePartnerRef } from '../lib/partnerAttribution'
import { useAuth } from '../context/AuthContext'
import { INCOME_CARD_CONFIG, CATEGORY_GROUPS } from '../hooks/incomeCardConfig'
import IncomeCard from './IncomeCard'
import { Field, NumberField } from './CalculatorFields'
import CalculatorResult from './CalculatorResult'
import { AutonomoResultSlot } from './AutonomoCalculatorPage'
import { AnonHeader, PartnerIntroScreen } from '../components/HomeLink'
import posthog from '../lib/posthog'

// ─── Sub-components ────────────────────────────────────────────────────────

function OptionButton({ label, selected, onClick }) {
  return (
    <button
      className={`triage-option${selected ? ' selected' : ''}`}
      onClick={onClick}
    >
      <span className="triage-option-indicator round">
        {selected && (
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {label}
    </button>
  )
}

function DropdownStep({ value, onChange, options, placeholder }) {
  return (
    <select
      className="cal-select"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{placeholder || 'Select…'}</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  )
}

function DependentsStep({ form }) {
  const kidsCount = Number(form.dependentsForm.children_under_25) || 0
  const ascCount  = Number(form.dependentsForm.ascendants_over_65) || 0

  return (
    <div className="cal-adjustments-section cal-dependents-section">
      <div className="cal-amount-row">
        <Field label="Children under 25">
          <NumberField
            value={form.dependentsForm.children_under_25}
            onChange={v => form.setDependent('children_under_25', v)}
          />
        </Field>
        {kidsCount > 0 && (
          <Field label="…how many under 3?">
            <NumberField
              value={form.dependentsForm.children_under_3}
              onChange={v => form.setDependent('children_under_3', v)}
              max={kidsCount}
            />
          </Field>
        )}
      </div>

      <div className="cal-amount-row">
        <Field label="Dependents 65+">
          <NumberField
            value={form.dependentsForm.ascendants_over_65}
            onChange={v => form.setDependent('ascendants_over_65', v)}
          />
        </Field>
        {ascCount > 0 && (
          <Field label="…how many 75+?">
            <NumberField
              value={form.dependentsForm.ascendants_over_75}
              onChange={v => form.setDependent('ascendants_over_75', v)}
              max={ascCount}
            />
          </Field>
        )}
      </div>

      <label className="cal-checkbox-label cal-checkbox-label--spaced">
        <input
          type="checkbox"
          checked={form.dependentsForm.disability_taxpayer}
          onChange={e => form.setDependent('disability_taxpayer', e.target.checked)}
        />
        I have a recognized disability (33% or higher)
      </label>
    </div>
  )
}

function AgeStep({ form }) {
  const stepCfg = form.currentStep
  const isMarried = form.answers.marital_status === 'married'

  return (
    <div className="cal-age-columns">
      <div className="cal-age-column">
        <div className="u-form-label">Your age</div>
        <div className="u-stack-xs">
          {stepCfg.options.map(opt => (
            <OptionButton
              key={String(opt.value)}
              label={opt.label}
              selected={form.answers.age === opt.value}
              onClick={() => form.set('age', opt.value)}
            />
          ))}
        </div>
      </div>

      {isMarried && (
        <div className="cal-age-column">
          <div className="u-form-label">{stepCfg.spouseQuestion}</div>
          {stepCfg.spouseHint && <p className="u-hint">{stepCfg.spouseHint}</p>}
          <div className="u-stack-xs">
            {stepCfg.options.map(opt => (
              <OptionButton
                key={String(opt.value)}
                label={opt.label}
                selected={form.answers.spouse_age === opt.value}
                onClick={() => form.set('spouse_age', opt.value)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Renders the citizenship_group step: primary self column, plus an EU-citizen
// follow-up question when the primary answer triggers it, plus a mirrored
// spouse column (with its own follow-up) when married. Mirrors AgeStep's
// two-column self/spouse layout exactly — same wrapper classes, same
// conditional spouse column gated on marital_status.
//
// BUG FIX: this component didn't exist before — Step1's renderInput() switch
// had no case for 'citizenship_group' (only single_select, select_dropdown,
// eligibility_group, dependents, age_group), so the citizenship question's
// header/hint rendered but no answer options ever did. The step still
// validated fine (useCalculatorForm.js's canProceed already has a
// citizenship_group case), so it was possible to get silently stuck on a
// step with nothing to click. Pre-existing gap, not introduced by the
// draft/publish changes — STEP_1_ORDER already included 'citizenship'.
function CitizenshipStep({ form }) {
  const stepCfg = form.currentStep
  const isMarried = form.answers.marital_status === 'married'

  return (
    <div className="cal-age-columns">
      <div className="cal-age-column">
        <div className="u-form-label">Your citizenship status</div>
        <div className="u-stack-xs">
          {stepCfg.options.map(opt => (
            <OptionButton
              key={String(opt.value)}
              label={opt.label}
              selected={form.answers[stepCfg.field] === opt.value}
              onClick={() => form.set(stepCfg.field, opt.value)}
            />
          ))}
        </div>
        {stepCfg.followUp && form.answers[stepCfg.field] === stepCfg.followUp.triggerValue && (
          <div className="u-stack-sm">
            <div className="u-form-label">{stepCfg.followUp.question}</div>
            {stepCfg.followUp.hint && <p className="u-hint">{stepCfg.followUp.hint}</p>}
            <div className="u-stack-xs">
              {stepCfg.followUp.options.map(opt => (
                <OptionButton
                  key={String(opt.value)}
                  label={opt.label}
                  selected={form.answers[stepCfg.followUp.field] === opt.value}
                  onClick={() => form.set(stepCfg.followUp.field, opt.value)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {isMarried && (
        <div className="cal-age-column">
          <div className="u-form-label">{stepCfg.spouseQuestion}</div>
          {stepCfg.spouseHint && <p className="u-hint">{stepCfg.spouseHint}</p>}
          <div className="u-stack-xs">
            {stepCfg.options.map(opt => (
              <OptionButton
                key={String(opt.value)}
                label={opt.label}
                selected={form.answers[stepCfg.spouseField] === opt.value}
                onClick={() => form.set(stepCfg.spouseField, opt.value)}
              />
            ))}
          </div>
          {stepCfg.spouseFollowUp && form.answers[stepCfg.spouseField] === stepCfg.spouseFollowUp.triggerValue && (
            <div className="u-stack-sm">
              <div className="u-form-label">{stepCfg.spouseFollowUp.question}</div>
              {stepCfg.spouseFollowUp.hint && <p className="u-hint">{stepCfg.spouseFollowUp.hint}</p>}
              <div className="u-stack-xs">
                {stepCfg.spouseFollowUp.options.map(opt => (
                  <OptionButton
                    key={String(opt.value)}
                    label={opt.label}
                    selected={form.answers[stepCfg.spouseFollowUp.field] === opt.value}
                    onClick={() => form.set(stepCfg.spouseFollowUp.field, opt.value)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// EarlyBlockScreen was removed in favor of inline per-row alerts in
// EligibilityGroupStep — staying on the same screen means a user who
// trips a block doesn't lose their other two eligibility answers by
// going back, which the old full-screen redirect did.

// "Not sure?" explainer text — informational only, not a stored answer value.
// Keeps Beckham/residency as clean Yes/No while still helping genuinely
// uncertain users, without reintroducing a third option that behaves
// identically to "No" downstream (which was the original contradiction).
const ELIGIBILITY_HELP_TEXT = {
  beckham_status: "Beckham Law isn't automatic — it requires an application that was approved by the Agencia Tributaria. If you never applied, or aren't sure whether your application was approved, the answer is almost always No.",
  residency: "Spain counts any part of a day spent in the country toward the 183-day threshold for the calendar year. If you arrived partway through the year and have been here 183+ days since, the answer is generally Yes. Still not sure? The full HolaSam assessment walks through residency status in more detail.",
}

function EligibilityGroupStep({ form, groupCfg }) {
  const [openHelp, setOpenHelp] = useState(null)
  // Tracks which fieldKeys have already fired calculator_blocked this
  // session, so flipping an answer back and forth doesn't spam duplicate
  // events — but re-blocking on a *different* field, or re-triggering after
  // clearing and re-answering, still fires again.
  const firedBlocksRef = useRef(new Set())

  useEffect(() => {
    groupCfg.fields.forEach(fieldKey => {
      const block = getEligibilityBlock(fieldKey, form.answers)
      if (block && !firedBlocksRef.current.has(fieldKey)) {
        firedBlocksRef.current.add(fieldKey)
        posthog.capture('calculator_blocked', { reason: fieldKey })
      } else if (!block) {
        firedBlocksRef.current.delete(fieldKey)
      }
    })
  }, [form.answers, groupCfg.fields])

  return (
    <div className="cal-eligibility-group">
      {groupCfg.fields.map(fieldKey => {
        const fieldCfg = STEP_1_CONFIG[fieldKey]
        const helpText = ELIGIBILITY_HELP_TEXT[fieldKey]
        const block = getEligibilityBlock(fieldKey, form.answers)
        return (
          <div className="cal-eligibility-row" key={fieldKey}>
            <div className="cal-eligibility-row-label">{fieldCfg.shortLabel || fieldCfg.question}</div>
            {fieldCfg.hint && <p className="cal-eligibility-row-hint">{fieldCfg.hint}</p>}
            {fieldCfg.type === 'select_dropdown' ? (
              <DropdownStep
                value={form.answers[fieldCfg.field]}
                onChange={v => form.set(fieldCfg.field, v)}
                options={fieldCfg.options}
                placeholder="Select a comunidad…"
              />
            ) : (
              <>
                <div className="u-stack-sm triage-options--compact triage-options--horizontal">
                  {fieldCfg.options.map(opt => (
                    <OptionButton
                      key={String(opt.value)}
                      label={opt.label}
                      selected={form.answers[fieldCfg.field] === opt.value}
                      onClick={() => form.set(fieldCfg.field, opt.value)}
                    />
                  ))}
                </div>
                {helpText && !block && (
                  <button
                    type="button"
                    className="cal-eligibility-help-link"
                    onClick={() => setOpenHelp(openHelp === fieldKey ? null : fieldKey)}
                  >
                    Not sure?
                  </button>
                )}
                {helpText && !block && openHelp === fieldKey && (
                  <p className="cal-eligibility-help-text">{helpText}</p>
                )}
              </>
            )}
            {block && (
              <div className="cal-eligibility-alert">
                <div className="cal-eligibility-alert-reason">{block.reason}</div>
                <a className="cal-eligibility-alert-cta" href="/spainsnapshot">{block.cta}</a>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function Step1({ form }) {
  const stepCfg  = form.currentStep
  const fieldKey = form.currentFieldKey

  function renderInput() {
    switch (stepCfg.type) {
      case 'single_select':
        return (
          <div className="u-stack-sm">
            {stepCfg.options.map(opt => (
              <OptionButton
                key={String(opt.value)}
                label={opt.label}
                selected={form.answers[stepCfg.field] === opt.value}
                onClick={() => form.set(stepCfg.field, opt.value)}
              />
            ))}
          </div>
        )

      case 'select_dropdown':
        return (
          <DropdownStep
            value={form.answers[stepCfg.field]}
            onChange={v => form.set(stepCfg.field, v)}
            options={stepCfg.options}
            placeholder="Select a comunidad…"
          />
        )

      case 'eligibility_group':
        return <EligibilityGroupStep form={form} groupCfg={stepCfg} />

      case 'citizenship_group':
        return <CitizenshipStep form={form} />

      case 'dependents':
        return <DependentsStep form={form} />

      case 'age_group':
        return <AgeStep form={form} />

      default:
        return null
    }
  }

  return (
    <div className="triage-form">
      <div className="triage-form-meta">
        <span className="triage-step-label">
          {stepCfg.type === 'eligibility_group'
            ? 'Eligibility checks'
            : `Question ${form.currentStepIndex + 1} of ${form.totalSteps}`}
        </span>
        {form.currentStepIndex > 0 && (
          <button className="triage-back-link" onClick={form.goBack}>← Back</button>
        )}
      </div>

      <h2 className="triage-question">{stepCfg.question}</h2>

      {stepCfg.hint && <p className="triage-hint">{stepCfg.hint}</p>}

      {renderInput()}

      {!form.canProceed && stepCfg.type === 'eligibility_group' && (
        <p className="cal-eligibility-continue-hint">
          {form.blockedState
            ? 'This calculator can\'t give a reliable estimate for your situation — see the note above.'
            : 'Answer all three checks to continue.'}
        </p>
      )}

      <button
        className="triage-next"
        onClick={form.goNext}
        disabled={!form.canProceed}
      >
        {form.isLastStep ? 'Continue to income →' : 'Continue →'}
      </button>
    </div>
  )
}

// Replaces the old "Just mine / Combined household" upfront question.
// That toggle gated Step 2 behind an extra question and, worse, sat
// alongside the real mechanism (the per-item owner toggle on each income
// card) — two competing "whose income" controls on the same screen.
// Now there's exactly one: tag each item as you add it. This is just an
// explanatory note, not a question — nothing to answer, nothing to miss.
// ── Owner-scoped income helpers ─────────────────────────────────────────────
// Storage stays one flat array per income_type (form.incomeItemsByType),
// mixing both owners — these helpers split/merge by owner at render time so
// Step2 can show only one person's items without changing the underlying
// form state shape.
function itemsForOwner(items, owner) {
  return items.filter(i => (i.owner || 'self') === owner)
}
function mergeOwnerItems(items, owner, newItemsForOwner) {
  return [...items.filter(i => (i.owner || 'self') !== owner), ...newItemsForOwner]
}
function hasAnyItemsForOwner(incomeItemsByType, owner) {
  return Object.values(incomeItemsByType).some(arr => itemsForOwner(arr, owner).length > 0)
}

// Step2 renders once for the primary filer ('self') and, only when married,
// a second time for the spouse ('spouse') — two separate pages, not one
// page with a per-item "whose income is this?" toggle. Ownership is
// implicit from which page a card lives on.
// Explicit yes/no page shown only to married users, between "your income"
// and "your spouse's income". Forces an active choice rather than letting
// the spouse-income page slide by unnoticed, and gives room to explain why
// we're asking before the user commits to filling out a second income page.
export function SpouseIncomeGate({ form, onSkip, onAdd, onBack }) {
  return (
    <div className="triage-form">
      <div className="triage-form-meta">
        <span className="triage-step-label">Your spouse's income</span>
        <button className="triage-back-link" onClick={onBack}>← Back</button>
      </div>

      <h2 className="triage-question">Does your spouse have income?</h2>
      <p className="triage-hint">
        Adding it lets us compare individual and joint filing and estimate which is
        more favorable for your household — even if your spouse has no income at all,
        filing jointly can still help in some cases.
      </p>

      <div className="cal-income-scope">
        <div className="u-row-md">
          <button className="cal-income-scope-btn" onClick={onSkip}>
            No, continue
          </button>
          <button className="cal-income-scope-btn cal-income-scope-btn--selected" onClick={onAdd}>
            Yes, add their income
          </button>
        </div>
      </div>
    </div>
  )
}

function Step2({ form, onBack, onNext, owner = 'self' }) {
  const isSpouseStep = owner === 'spouse'

  // All income cards are always shown — is_us_person is now derived from
  // which cards the user fills in, not asked as a separate question.
  // US-specific cards (retirement distributions, US entity income) are
  // labeled clearly enough that non-US users won't fill them in.
  const visibleCards = INCOME_CARD_CONFIG

  // Group visible cards by category, preserving CATEGORY_GROUPS order
  const groupedCards = CATEGORY_GROUPS
    .map(group => ({
      group,
      cards: visibleCards.filter(c => c.category === group.key),
    }))
    .filter(({ cards }) => cards.length > 0)

  const hasItems = hasAnyItemsForOwner(form.incomeItemsByType, owner)
  // The self page still requires at least one item. The spouse page is
  // reached only after the user actively chose "Yes, add their income" on
  // SpouseIncomeGate, so it's fine to continue with zero items here too if
  // they change their mind mid-page — no separate empty-state messaging
  // needed, that job belongs to the gate page.
  const canContinue = isSpouseStep ? true : hasItems

  return (
    <div className="triage-form">
      <div className="triage-form-meta">
        <span className="triage-step-label">
          {isSpouseStep ? "Your spouse's income" : 'Step 2 of 3 — Your income'}
        </span>
        <button className="triage-back-link" onClick={onBack}>← Back</button>
      </div>

      <h2 className="triage-question">
        {isSpouseStep ? "Add your spouse's income" : 'What income do you have?'}
      </h2>
      <p className="triage-hint">
        {isSpouseStep
          ? 'Add anything your spouse expects to receive this year.'
          : 'Select all that apply. You can add more than one of the same type — ' +
            'for example, two rental properties.'}
      </p>

      <div className="cal-income-categories">
        {groupedCards.map(({ group, cards }) => (
          <div className="cal-income-category" key={group.key}>
            <div className="cal-income-category-header">
              <div className="cal-income-category-icon-chip">
                <i className={`ti ${group.tablerIcon}`} aria-hidden="true" />
              </div>
              <div>
                <div className="u-label-base">{group.label}</div>
                <div className="cal-income-category-desc">{group.description}</div>
              </div>
            </div>
            <div className="cal-income-card-grid">
              {cards.map(cardConfig => {
                const allItemsForType = form.incomeItemsByType[cardConfig.income_type] || []
                return (
                  <IncomeCard
                    key={cardConfig.income_type}
                    cardConfig={cardConfig}
                    items={itemsForOwner(allItemsForType, owner)}
                    onItemsChange={items =>
                      form.setIncomeItems(cardConfig.income_type, mergeOwnerItems(allItemsForType, owner, items))
                    }
                    adjustmentsForm={form.adjustmentsForm}
                    setAdjustment={form.setAdjustment}
                    owner={owner}
                  />
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {!isSpouseStep && !hasItems && (
        <p className="cal-income-empty-hint">
          Select at least one income type above to continue.
        </p>
      )}

      <button
        className="triage-next"
        onClick={onNext}
        disabled={!canContinue}
      >
        Continue →
      </button>
    </div>
  )
}

function Step4({ form, onBack, partnerRef }) {
  if (form.submitting) {
    return (
      <div className="triage-form">
        <div className="triage-form-meta">
          <span className="triage-step-label">Step 3 of 3 — Your estimate</span>
        </div>
        <div className="asmt-state asmt-state--calculating">
          <div className="app-spinner" />
          <p className="triage-hint triage-hint--centered">
            Calculating your estimate…
          </p>
        </div>
      </div>
    )
  }

  if (form.submitError) {
    return (
      <div className="triage-form">
        <div className="triage-form-meta">
          <span className="triage-step-label">Step 3 of 3 — Your estimate</span>
          <button className="triage-back-link" onClick={onBack}>← Back</button>
        </div>
        <h2 className="triage-question">Something went wrong</h2>
        <div className="triage-submit-error">{form.submitError}</div>
        <button className="triage-next" onClick={() => form.runCalculation({ partnerRef })}>
          Try again →
        </button>
      </div>
    )
  }

  if (!form.result) {
    return (
      <div className="triage-form">
        <div className="triage-form-meta">
          <span className="triage-step-label">Step 3 of 3 — Your estimate</span>
          <button className="triage-back-link" onClick={onBack}>← Back</button>
        </div>
        <h2 className="triage-question">Your estimate</h2>
        <p className="triage-hint">Ready when you are.</p>
        <button className="triage-next" onClick={() => form.runCalculation({ partnerRef })}>
          See my estimate →
        </button>
      </div>
    )
  }

  return null
}

// ─── Step 3: FX rate confirmation ───────────────────────────────────────────
// Only rendered when the session contains non-EUR income items.
// Shows each detected currency with its default rate and an optional override input.

export function Step3({ form, onBack, onNext }) {
  const [useCustomRates, setUseCustomRates] = useState(
    // Pre-select "custom" if user already has overrides from a prior visit to this step
    Object.keys(form.fxRateOverrides).length > 0
  )

  // Per-currency draft input values — strings so the number field stays editable.
  // Initialised from whatever the form currently has (override or default).
  const [draftRates, setDraftRates] = useState(() => {
    const init = {}
    for (const currency of form.detectedCurrencies) {
      const override = form.fxRateOverrides[currency]
      init[currency] = override !== undefined
        ? String(override)
        : String(DEFAULT_FX_RATES[currency] ?? '')
    }
    return init
  })

  function handleToggle(custom) {
    setUseCustomRates(custom)
    if (!custom) {
      // Revert all currencies to defaults
      form.detectedCurrencies.forEach(c => form.setFxRate(c, null))
    }
  }

  function handleRateChange(currency, value) {
    setDraftRates(prev => ({ ...prev, [currency]: value }))
    const parsed = parseFloat(value)
    if (!isNaN(parsed) && parsed > 0) {
      form.setFxRate(currency, parsed)
    }
  }

  const canProceed = !useCustomRates || form.detectedCurrencies.every(c => {
    const val = parseFloat(draftRates[c])
    return !isNaN(val) && val > 0
  })

  // Currency display helper — e.g. 'USD' -> 'US Dollar (USD)'
  const CURRENCY_NAMES = { USD: 'US Dollar', GBP: 'British Pound', CHF: 'Swiss Franc', CAD: 'Canadian Dollar', MXN: 'Mexican Peso' }

  return (
    <div className="triage-form">
      <div className="triage-form-meta">
        <span className="triage-step-label">Review assumptions</span>
        <button className="triage-back-link" onClick={onBack}>← Back</button>
      </div>

      <h2 className="triage-question">Confirm currency rates</h2>
      <p className="triage-hint">
        We noticed you entered income in {form.detectedCurrencies.length === 1
          ? form.detectedCurrencies[0]
          : form.detectedCurrencies.join(' and ')}.
        These planning rates will be used to convert to euros — they are not
        official annual average rates or transaction-date rates.
      </p>

      <div className="cal-fx-table">
        <div className="cal-fx-table-header">
          <span>Currency</span>
          <span>Rate to EUR</span>
          {useCustomRates && <span>Your rate</span>}
        </div>
        {form.detectedCurrencies.map(currency => {
          const defaultRate = DEFAULT_FX_RATES[currency]
          const currencyLabel = CURRENCY_NAMES[currency]
            ? `${CURRENCY_NAMES[currency]} (${currency})`
            : currency
          return (
            <div className="cal-fx-table-row" key={currency}>
              <span className="u-label-sm">{currencyLabel}</span>
              <span className="cal-fx-default-rate">
                {defaultRate !== undefined
                  ? `1 ${currency} = ${defaultRate} EUR`
                  : 'No default available'}
              </span>
              {useCustomRates && (
                <span className="cal-fx-input-cell">
                  <input
                    className="cal-fx-input"
                    type="number"
                    step="0.001"
                    min="0.001"
                    value={draftRates[currency] ?? ''}
                    onChange={e => handleRateChange(currency, e.target.value)}
                    placeholder={defaultRate !== undefined ? String(defaultRate) : '0.000'}
                  />
                  <span className="cal-fx-input-label">EUR per 1 {currency}</span>
                </span>
              )}
            </div>
          )
        })}
      </div>

      <div className="cal-fx-options">
        <button
          className={`cal-fx-option${!useCustomRates ? ' cal-fx-option--selected' : ''}`}
          onClick={() => handleToggle(false)}
        >
          <span className="cal-fx-option-indicator">{!useCustomRates && '●'}</span>
          Use HolaSam planning rates
        </button>
        <button
          className={`cal-fx-option${useCustomRates ? ' cal-fx-option--selected' : ''}`}
          onClick={() => handleToggle(true)}
        >
          <span className="cal-fx-option-indicator">{useCustomRates && '●'}</span>
          Enter my own rates
        </button>
      </div>

      <button
        className="triage-next"
        onClick={onNext}
        disabled={!canProceed}
      >
        Calculate my estimate →
      </button>
    </div>
  )
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function CalculatorPage() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const form      = useCalculatorForm()
  const { mode, isAuthenticated, authLoading, otherProductDone } = useProductMode()
  const { redirectAfterSignIn } = useAuth()

  // No more intro screen — everyone lands on step1 (see file header).
  const [screen, setScreen] = useState('step1')
  const [animIn, setAnimIn] = useState(true)

  // ── Partner-linked capture ──────────────────────────────────────────────
  // partnerRef now reads from the persistent cookie (see
  // src/lib/partnerAttribution.js) instead of parsing location.search
  // directly — the cookie is captured once, app-wide, in AppShell.js, and
  // survives navigation to a page whose URL no longer has ?ref= in it
  // (which this raw location.search read never did). Resolved once on
  // mount, then threaded through to the save/calculate calls below and
  // passed to CalculatorResult so ResultCTA can render the partner-linked
  // treatment.
  const [partner, setPartner] = useState(null)
  // Disambiguates "lookup still pending" from "lookup resolved, no matching
  // partner row" (a garbage/typo'd ?ref=). Flips true once the .then() below
  // fires, whether or not a row was found — see ticket 014-partnerintro D6.
  const [partnerChecked, setPartnerChecked] = useState(false)
  const partnerRef = getActivePartnerRef()

  useEffect(() => {
    if (!partnerRef) return
    fetchPartnerNameByRef(partnerRef)
      .then(({ data }) => {
        if (data) setPartner(data)
        setPartnerChecked(true)
      })
    // CHANGED from [] — see ticket 014-partnerintro's "Timing verification"
    // finding 3. With [], a direct first-ever /calculator?ref=… landing
    // could permanently miss this lookup (partnerRef read null at the
    // moment this effect's closure was created); now that showPartnerIntro
    // waits on partnerChecked (D6), that would hang the intro-screen
    // spinner forever instead of just silently skipping the partner-name
    // pill.
  }, [partnerRef])

  // ── Partner-referral intro screen (ticket 014-partnerintro) ────────────
  // likelyPartnerLinked is used only to decide whether to show the loading
  // spinner below — never for attribution itself, which stays exclusively
  // cookie-based via partnerRef once authLoading is false. See the plan's
  // "Timing verification" finding 2 for why the raw ?ref= read is needed
  // alongside the cookie.
  const likelyPartnerLinked = !!(
    partnerRef || new URLSearchParams(window.location.search).get('ref')
  )
  const [showPartnerIntro, setShowPartnerIntro] = useState(null) // null = undecided

  useEffect(() => {
    if (authLoading) return
    if (!partnerRef) { setShowPartnerIntro(false); return }
    if (isAuthenticated) { setShowPartnerIntro(false); return }
    if (!partnerChecked) return // still waiting on the partner-name fetch — keep the spinner up
    // partnerChecked is true now. Either a real partner resolved, or the
    // ref was garbage/unrecognized (partner still null) — in that case,
    // fall straight through to the real first question, same as if
    // partnerRef had been absent, rather than showing a broken/placeholder
    // sentence.
    setShowPartnerIntro(!!partner)
  }, [authLoading, partnerRef, isAuthenticated, partnerChecked, partner])

  // If navigated back from saved result page, restore session and jump to step2
  useEffect(() => {
    const restored = location.state?.restoreSession
    if (!restored) return

    // Only ever present when resuming a DRAFT specifically (IntelligencePage.js's
    // "your calculator scenarios" list) — never sent by the published-result
    // "← back to edit" flow, which must keep forking into a new draft on next
    // recalculate (existing behavior, unchanged). Seeding this turns the next
    // recalculate into an UPDATE of the original row instead of an INSERT —
    // without it, resuming a draft would restore its answers but silently
    // create a second, brand-new draft the moment the user recalculates,
    // leaving the one they clicked into still orphaned.
    if (location.state?.restoreSessionId) {
      form.setSessionId(location.state.restoreSessionId)
      if (location.state.restoreResult)             form.setResult(location.state.restoreResult)
      if (location.state.restoreAutonomoResult)      form.setAutonomoResult(location.state.restoreAutonomoResult)
      if (location.state.restoreFilingComparison)    form.setFilingComparison(location.state.restoreFilingComparison)
      if (location.state.restoreAutonomoIrpfImpact)  form.setAutonomoIrpfImpact(location.state.restoreAutonomoIrpfImpact)
    }

    const pf = restored.profile_facts
    const ty = restored.tax_year_facts
    if (pf) {
      // is_us_person: not restored — derived from income items on the next render.
      form.set('marital_status', pf.marital_status)
      form.set('age',            pf.age)
      form.set('spouse_age',     pf.spouse_age)
      form.set('comunidad',      pf.comunidad)
      form.set('beckham_status', pf.beckham_status)
    }
    if (ty) {
      // tax_year: not restored — always CURRENT_BRACKET_YEAR.
      form.set('residency_year_context', ty.residency_year_context)
    }

    if (restored.income_items?.length > 0) {
      const byType = {}
      for (const item of restored.income_items) {
        if (!byType[item.income_type]) byType[item.income_type] = []
        byType[item.income_type].push(item)
      }
      for (const [type, items] of Object.entries(byType)) {
        form.setIncomeItems(type, items)
      }
      // Not part of profile_facts (this is a pure navigation-state field,
      // not sent to the engine) — infer it from whether any restored item
      // is spouse-owned, so the back button from step3/step4 lands on the
      // right screen after reopening a saved session.
      if (restored.income_items.some(i => i.owner === 'spouse')) {
        form.set('spouse_income_choice', 'added')
      }
    }

    if (restored.adjustments) {
      form.setAdjustment('pension_contributions',   restored.adjustments.pension_contributions   || '')
      form.setAdjustment('quarterly_payments',      restored.adjustments.quarterly_payments      || '')
      form.setAdjustment('additional_withholdings', restored.adjustments.additional_withholdings || '')
    }
    if (restored.dependents) {
      form.setDependent('children_under_25',  restored.dependents.children_under_25  || '')
      form.setDependent('children_under_3',   restored.dependents.children_under_3   || '')
      form.setDependent('ascendants_over_65', restored.dependents.ascendants_over_65 || '')
      form.setDependent('ascendants_over_75', restored.dependents.ascendants_over_75 || '')
      form.setDependent('disability_taxpayer', !!restored.dependents.disability_taxpayer)
    }

    // BUG FIX: fx_rates_used was never restored here, so fxRateOverrides
    // stayed at its default {} after "Back" from a saved result — Step3
    // would then compute useCustomRates as false (no overrides present)
    // and show every rate reset to the default, even though the original
    // session had a custom rate. Only restore entries actually tagged
    // 'user_override' — 'default_planning_rate' entries aren't overrides
    // and restoring them as such would make Step3 show "custom" selected
    // with values the user never actually typed in.
    if (restored.fx_rates_used) {
      for (const [currency, entry] of Object.entries(restored.fx_rates_used)) {
        if (entry?.source === 'user_override') {
          form.setFxRate(currency, entry.rate_to_eur)
        }
      }
    }

    setScreen(location.state?.restoreSessionId ? 'step4' : 'step2')
    navigate(location.pathname, { replace: true, state: {} })
  }, [])

  function transition(next) {
    setAnimIn(false)
    setTimeout(() => { setScreen(next); setAnimIn(true) }, 180)
  }

  // Forward-progress-only wrapper around transition() — fires
  // calculator_step_completed with the step just finished. Deliberately
  // not used for onBack transitions; going backward isn't "completing" a
  // step and would muddy the funnel.
  function completeStep(step, next) {
    posthog.capture('calculator_step_completed', { step })
    transition(next)
  }

  function handleStep1Next() {
    if (form.blockedState) return
    if (form.isLastStep) completeStep('step1', 'step2')
    else form.goNext()
  }

  // No intro to fall back to anymore at currentStepIndex === 0 — Step1
  // itself only renders "← Back" when currentStepIndex > 0 (see Step1's
  // own definition above), so there was never a reachable UI path that
  // invoked the old "transition('intro')" branch in the first place. This
  // mirrors AutonomoCalculatorPage.js's PersonalFactsStep.handleBack,
  // which never had an intro to begin with.
  function handleStep1Back() {
    if (form.currentStepIndex > 0) form.goBack()
  }

  // Auth-aware calculate-and-save.
  // Authenticated: passes JWT so the edge function saves the row as an
  //   owned draft immediately (owner_user_id set, published_at null).
  //   It stays a draft — no automatic navigation to a permanent URL —
  //   until the user explicitly publishes from the result sidebar.
  // Anonymous: runs calculation only; an anonymous draft row is created
  //   the same way, just without an owner. Publishing for anonymous users
  //   happens via email capture in the sidebar (saveSession).
  async function runAndSave() {
    if (isAuthenticated) {
      const { data: { session: authSession } } = await supabase.auth.getSession()
      const token = authSession?.access_token || null
      await form.runCalculation({ authToken: token, partnerRef })
    } else {
      await form.runCalculation({ partnerRef })
    }
  }

  // Explicit publish action for authenticated users — wired to the
  // "Save & get link" button in CalculatorResult's sidebar. Mints
  // public_token + published_at on the current draft and gives the user
  // a permanent, shareable URL for the first time. Anonymous publish goes
  // through the existing onSave (email capture) path instead — see
  // PublicSidebar in CalculatorResult.js — so this only needs to handle
  // the authenticated case.
  async function handlePublish() {
    // No isAuthenticated guard: that flag is computed once at mount and goes
    // stale when an anonymous user connects via the advisor OTP flow on a
    // draft. The real Supabase session is the source of truth; throw so
    // callers (ResultCTA's Save button) can show an error.
    const { data: { session: authSession } } = await supabase.auth.getSession()
    if (!authSession) throw new Error('Not signed in')
    const token = authSession.access_token
    const publicToken = await form.publishSession(token)
    if (publicToken) navigate(`/calculator/result/${publicToken}`, { replace: true })
  }

  const showWideResult = screen === 'step4' && form.result && !form.submitting && !form.submitError

  // Loading gate — only for visitors who might be partner-referred (see
  // likelyPartnerLinked above). Organic/non-referred visitors never hit
  // this and see zero behavior change.
  if (likelyPartnerLinked && showPartnerIntro === null) {
    return (
      <div className="asmt-state"><div className="app-spinner" /></div>
    )
  }

  return (
    <div className="triage-root">

      {screen === 'step1' && showPartnerIntro !== true && (
        <div className="triage-progress-track">
          <div className="triage-progress-fill" style={{ width: `${form.progressPct}%` }} />
        </div>
      )}


      {showWideResult ? (
        <div className={`cal-fade-wrapper${animIn ? ' cal-fade-wrapper--in' : ''}`}>
          <CalculatorResult
            result={form.result}
            session={form.buildSession()}
            filingComparison={form.filingComparison}
            onBack={() => transition('step2')}
            onEditFxAssumptions={form.hasForeignCurrencyIncome ? () => transition('step3') : null}
            // Pass onSave=null for authenticated users — email capture is suppressed
            // in CalculatorResult when onSave is null.
            onSave={isAuthenticated ? null : (email) => form.saveSession(email, partnerRef)}
            isAuthenticated={isAuthenticated}
            ownerState={isAuthenticated ? 'owned' : 'public'}
            published={form.published}
            onPublish={handlePublish}
            publicToken={form.savedToken}
            sessionId={form.sessionId}
            isPartnerLinked={!!partner}
            partnerName={partner?.name}
            product="calculator"
            otherProductDone={otherProductDone('calculator')}
            afterHeaderContent={<AutonomoResultSlot form={form} />}
          />
        </div>
      ) : (
        <div
          className={`triage-col cal-fade-wrapper${animIn ? ' cal-fade-wrapper--in' : ''}`}
        >

          {/* Persistent for every anonymous view of this page now (was
              previously only reachable via the deleted intro screen).
              Renders ProductTag inline next to the wordmark itself now —
              see AnonHeader's own comment in HomeLink.js. */}
          <AnonHeader show={!isAuthenticated} product="calculator" partnerName={partner?.name} onSignInSuccess={redirectAfterSignIn} />

          {/* ── Partner-referral intro (ticket 014-partnerintro), or Step 1 ── */}
          {showPartnerIntro ? (
            <PartnerIntroScreen
              partnerName={partner?.name}
              onContinue={() => setShowPartnerIntro(false)}
            />
          ) : (
            screen === 'step1' && (
              <Step1
                form={{ ...form, goNext: handleStep1Next, goBack: handleStep1Back }}
              />
            )
          )}

          {/* ── Step 2: Income ── */}
          {screen === 'step2' && (
            <Step2
              form={form}
              owner="self"
              onBack={() => transition('step1')}
              onNext={() => {
                if (form.answers.marital_status === 'married') {
                  completeStep('step2', 'step2_gate')
                } else if (form.hasForeignCurrencyIncome) {
                  completeStep('step2', 'step3')
                } else {
                  completeStep('step2', 'step4')
                  runAndSave()
                }
              }}
            />
          )}

          {/* ── Step 2 gate: does your spouse have income? (married only) ── */}
          {screen === 'step2_gate' && (
            <SpouseIncomeGate
              form={form}
              onBack={() => transition('step2')}
              onSkip={() => {
                form.set('spouse_income_choice', 'skipped')
                posthog.capture('calculator_step_completed', { step: 'step2_gate_skipped' })
                if (form.hasForeignCurrencyIncome) transition('step3')
                else { transition('step4'); runAndSave() }
              }}
              onAdd={() => {
                form.set('spouse_income_choice', 'added')
                completeStep('step2_gate', 'step2b')
              }}
            />
          )}

          {/* ── Step 2b: Spouse's income (only reached if they chose "Yes, add their income") ── */}
          {screen === 'step2b' && (
            <Step2
              form={form}
              owner="spouse"
              onBack={() => transition('step2_gate')}
              onNext={() => {
                if (form.hasForeignCurrencyIncome) {
                  completeStep('step2b', 'step3')
                } else {
                  completeStep('step2b', 'step4')
                  runAndSave()
                }
              }}
            />
          )}

          {/* ── Step 3: FX assumptions (only shown when foreign currency income present) ── */}
          {screen === 'step3' && (
            <Step3
              form={form}
              onBack={() => transition(
                form.answers.marital_status === 'married'
                  ? (form.answers.spouse_income_choice === 'added' ? 'step2b' : 'step2_gate')
                  : 'step2'
              )}
              onNext={() => { completeStep('step3', 'step4'); runAndSave() }}
            />
          )}

          {/* ── Step 4: Estimate (loading / error / empty states) ── */}
          {screen === 'step4' && (
            <Step4 form={form} partnerRef={partnerRef} onBack={() => {
              if (form.hasForeignCurrencyIncome) {
                transition('step3')
              } else if (form.answers.marital_status === 'married') {
                transition(form.answers.spouse_income_choice === 'added' ? 'step2b' : 'step2_gate')
              } else {
                transition('step2')
              }
            }} />
          )}

        </div>
      )}
    </div>
  )
}